import { useBreakpoint } from './useBreakpoint'

/**
 * The one definition of how tall a touch target is.
 *
 * `Button` owned this map privately and `Sidebar` wrote its own `44 : 33`. Two
 * mechanisms answering "how tall is a touch target" is how they drift, and #90 —
 * which lifts `Button` to 44px below 640px — would have had to be applied twice
 * without noticing the second.
 *
 * **Sizes:** 33px page, 30px panel, 24px inline, 33px form. The 24px inline
 * target is below the 44px floor even on desktop, deliberately — it is for
 * dense desktop rows.
 *
 * **`form` shares `page`'s 33 and that is not a duplicate to be collapsed.**
 * This map answers one question — the ACCESSIBILITY FLOOR — and the answer for
 * a form control is genuinely the same 33. What differs is the height the
 * design draws, which comes from padding rather than from a floor: the auth
 * screens' inputs are `12px 14px` and their submit is `13px`, landing at
 * ~42px, where `page` carries no vertical padding at all and sits ON the floor
 * at 33. Merging the two names would merge a floor with a height, and the next
 * change to either would move both.
 *
 * Issue 271. Measured from the complete App source: 11 of the 12 uses of
 * `padding:12px 14px` on an input are on the pre-login screens.
 */
export const HIT_TARGET = { page: 33, panel: 30, inline: 24, form: 33 } as const

export type HitTargetSize = keyof typeof HIT_TARGET

/** M0-R29: hit targets rise to 44px below 640px. */
export const COARSE_HIT_TARGET = 44

/**
 * The height a control should reserve at the current viewport.
 *
 * `useBreakpoint()` is the single breakpoint source CLAUDE.md names. A media
 * query cannot do this job: the height is written inline so a caller cannot
 * shrink it under the documented target, and #90 measured an inline declaration
 * beating a media query at an emulated 375px — the box stayed 30px with the
 * query present and became 44px only when the inline value was removed.
 *
 * **`Button` consumes this since #90**, as do `Sidebar`, `SettingsLayout`,
 * `Field`, `NotificationsDrawer`, `MfaChallenge`, `BottomTabs`, `InlineLink`
 * and the top-bar avatar. It is the one answer to "how tall is a touch target",
 * which is the whole point: `Sidebar` had written its own `44 : 33` and #90
 * would otherwise have had to be applied twice.
 */
export function useHitTarget(size: HitTargetSize): number {
  return useBreakpoint() === 'phone' ? COARSE_HIT_TARGET : HIT_TARGET[size]
}

/**
 * The marker a control carries when it is built to the 30px `panel` floor but is
 * not a `Button`, so it cannot take `btn--size-panel`: that class also writes
 * `padding: 6px 14px` and a font size, which would balloon a 30px icon-only
 * remove button to 40px wide. Carries NO declarations; it exists so the
 * hit-target harness can read the floor a control declares instead of falling
 * back to the strictest (`e2e/hitTargets.mjs`'s `fineFloorFor` must list it).
 */
export const PANEL_HIT_CLASS = 'hit-panel'

/**
 * The marker and the floor it claims, returned together so one cannot exist
 * without the other. A control that writes `PANEL_HIT_CLASS` and `useHitTarget`
 * separately can later put the marker on a 33px control and loosen the sweep
 * with no test noticing; spreading this makes the class and the `minHeight`
 * come from the same call, and `minHeight` is `HIT_TARGET.panel` (30) at desktop
 * and the coarse 44 on a phone, exactly as `useHitTarget('panel')` answers.
 */
export function usePanelHit(): { className: typeof PANEL_HIT_CLASS; minHeight: number } {
  return { className: PANEL_HIT_CLASS, minHeight: useHitTarget('panel') }
}
